import React, { useEffect, useState } from 'react';
import './currentlyWorking.css';
import Table from '../../../Table/Table';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../../utils/axiosInstance';
import { formatISOToLocalTime, normalizeDbTimestampToUtcIso } from '../../../../utils/timeUtils';

const CurrentlyWorking = ({ project, employeeId }) => {
  const { t } = useTranslation();
  const [assignmentIds, setAssignmentIds] = useState([]);
  const [employeeLogins, setEmployeeLogins] = useState({});
  const [userTimeZone, setUserTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);

  useEffect(() => {
    const detectTimezone = () => {
      const detectedZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detectedZone !== userTimeZone) {
        setUserTimeZone(detectedZone);
      }
    };

    window.addEventListener('focus', detectTimezone);

    return () => {
      window.removeEventListener('focus', detectTimezone);
    };
  }, [userTimeZone]);

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        const response = await axiosInstance.get('/admin/empProjRelation');
        const data = await response.data;

        // Filter the assignments by the project ID
        const filteredAssignments = data.filter(assignment => assignment.project_id === project.id);

        // Extract the assignment IDs
        const ids = filteredAssignments.map(assignment => assignment.assign_id);

        // Store the assignment IDs in the state
        setAssignmentIds(ids);
      } catch (error) {
        console.error('Error fetching assignments:', error);
      }
    };

    fetchAssignments();
  }, [project.id]);

  useEffect(() => {
    const fetchEmployeeLogins = async () => {
      if (assignmentIds.length === 0) return;

      try {
        const response = await axiosInstance.get('/admin/empProjWork');
        const data = await response.data;

        // Define today's date
        const today = new Date();
        const todayDateString = today.toISOString().split('T')[0];
        const loginTimes = {};

        // Filter work entries by assignment IDs and today's date
        const filteredEntries = data.filter(entry => {
          const workDateLocal = new Date(entry.work_date).toISOString().split('T')[0];
          return assignmentIds.includes(entry.assign_id) && workDateLocal === todayDateString;
        });

        // Find the login time for each employee who is currently working (no time_stop)
        filteredEntries.forEach(entry => {
          if (!entry.time_stop && !loginTimes[entry.emp_name]) {
            const normalizedTime = normalizeDbTimestampToUtcIso(entry.time_start, entry.work_date);
            loginTimes[entry.p_name] = normalizedTime ? formatISOToLocalTime(normalizedTime) : '-';
          }
        });

        setEmployeeLogins(loginTimes);
      } catch (error) {
        console.error('Error fetching employee work logins:', error);
      }
    };

    fetchEmployeeLogins();
  }, [assignmentIds]);

  // Format timezone for display
  const formatTimeZone = () => {
    try {
      const timeZoneAbbr = new Intl.DateTimeFormat('en', {
        timeZoneName: 'short',
        timeZone: userTimeZone
      }).formatToParts().find(part => part.type === 'timeZoneName')?.value;

      return timeZoneAbbr || userTimeZone.split('/').pop();
    } catch (e) {
      return userTimeZone.split('/').pop();
    }
  };

  const headers = [t("projectDetails_EmployeeID"), t("attendance_name"), t("attendance_login")];

  // Generating data from project employees
  const data = project.employees.map((employee) => [
    employee.id.toString(),
    employee.name,
    employeeLogins[employee.name] || '-' // Use login time or "-" if not found
  ]);

  // Calculate the number of active employees
  const activeEmployeesCount = project.employees.filter(employee => employeeLogins[employee.name]).length;

  return (
    <div className='CurrentlyWorking-body'>
      <div className='CurrentlyWorking-header'>
        <span className='CurrentlyWorking-headerTitle'>{t("projectDetails_currentWorkingTitle")}</span>
        <span className='CurrentlyWorking-active'>
          {activeEmployeesCount} {t("projectDetails_currentWorkingNumber")}
        </span>
        {/* <span className='CurrentlyWorking-timezone'>
          ({t("Times shown in")} {formatTimeZone()})
        </span> */}
      </div>
      <div className='CurrentlyWorking-table-body' style={{ overflowX: 'auto' }}>
        <Table headers={headers} data={data} />
      </div>
    </div>
  );
};

export default CurrentlyWorking;